import type { GuestRatingSummary } from '@apps/main/new-desktop/modules/customersRating';
import type {
  PriceCalendarState,
  PriceRibbonMonth,
} from '@apps/main/new-desktop/modules/calendars';

import type { BoardBasisCode, FullStars } from '@libs/trip-planner-interfaces';

export interface PeekPriceModalCalendar {
  priceCalendarState: PriceCalendarState;
  selectedMonth: string;
  months: PriceRibbonMonth[];
}

export const enum PeekPriceModalItineraryType {
  Placeholder = 'placeholder',
  HotelOnly = 'hotelOnly',
  FlightPlusHotel = 'flightPlusHotel',
}

export interface PeekPriceModalPlaceholderItinerary {
  type: PeekPriceModalItineraryType.Placeholder;
  hotelName: string;
}

export interface PeekPriceRoomDetails {
  description: string;
  boardBasisCode: BoardBasisCode;
}

export interface PeekPriceModalHotelOnlyItinerary {
  type: PeekPriceModalItineraryType.HotelOnly;
  dates: string;
  price: string;
  paxes: string;
  hotel: PeekPriceHotelCardData;
}

export interface PeekPriceModalFlightPlusHotelItinerary {
  type: PeekPriceModalItineraryType.FlightPlusHotel;
  tripDeparture: string;
  dates: string;
  price: string;
  paxes: string;
  departureTransport: PeekPriceBookableTransportCardData;
  hotel: PeekPriceHotelCardData;
  arrivalTransport: PeekPriceBookableTransportCardData;
}

export type PeekPriceModalCustomItinerary =
  | PeekPriceModalHotelOnlyItinerary
  | PeekPriceModalFlightPlusHotelItinerary;

export type PeekPriceModalItinerary =
  | PeekPriceModalCustomItinerary
  | PeekPriceModalPlaceholderItinerary;

export function isPeekPriceModalPlaceholderItinerary(
  itinerary: PeekPriceModalItinerary
): itinerary is PeekPriceModalPlaceholderItinerary {
  return itinerary.type === PeekPriceModalItineraryType.Placeholder;
}

export function isPeekPriceModalHotelOnlyItinerary(
  itinerary: PeekPriceModalItinerary
): itinerary is PeekPriceModalHotelOnlyItinerary {
  return itinerary.type === PeekPriceModalItineraryType.HotelOnly;
}

export function isPeekPriceModalFlightPlusHotelItinerary(
  itinerary: PeekPriceModalItinerary
): itinerary is PeekPriceModalFlightPlusHotelItinerary {
  return itinerary.type === PeekPriceModalItineraryType.FlightPlusHotel;
}

export interface PeekPriceBookableTransportCardData {
  date: string;
  direction: string;
  stops: string;
  carrierLogoUrl: string;
  time: string;
  ports: string;
}

export interface PeekPriceHotelCardData {
  dates: string;
  imageUrls: string[];
  nights: string;
  hotelName: string;
  hotelDestination: string;
  hotelStars: FullStars;
  rooms: PeekPriceRoomDetails[] | undefined;
  guestRating?: GuestRatingSummary;
}
